import { Text } from '@/components/text';
import { PortalDropdown } from '@/components/ui/portal-dropdown';
import { Tooltip } from '@/components/ui/tooltip';
import { Colors } from '@/constants/theme';
import { useCountryName } from '@/hooks/useCountryName';
import type { Country } from '@/types/country';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BlurEvent, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';

type Props = {
  label: string;
  tooltip?: string;
  countries: Country[];
  valueId: string | null;
  onChange: (c: Country | null) => void;
  placeholder?: string;
  disabled?: boolean;
  excludeIds?: string[];
  disabledIds?: string[];
  maxResults?: number;
  hideClearButton?: boolean;
  id?: string;
  name?: string;
};

function normalize(s: string) {
  return s.trim().toLowerCase();
}

export function CountrySelect({
  label,
  tooltip,
  countries,
  valueId,
  onChange,
  placeholder,
  disabled,
  excludeIds,
  disabledIds,
  maxResults = 500,
  hideClearButton = false,
  id,
  name,
}: Props) {
  const { t } = useTranslation();
  const getLocalizedName = useCountryName();

  const selected = useMemo(() => countries.find(c => c.id === valueId) ?? null, [countries, valueId]);

  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');

  const pointerDownInsideRef = useRef(false);
  const containerRef = useRef<View>(null);
  const anchorRef = useRef<View>(null);

  const excludedSet = useMemo(() => new Set(excludeIds ?? []), [excludeIds]);
  const disabledSet = useMemo(() => new Set(disabledIds ?? []), [disabledIds]);

  const filtered = useMemo(() => {
    const query = normalize(q);
    const base = countries.filter(c => !excludedSet.has(c.id));

    if (!query) return base.slice(0, maxResults);

    const out: Country[] = [];
    for (const c of base) {
      const name = getLocalizedName(c).toLowerCase();
      const id = c.id.toLowerCase();
      const cur = (c.currencyISO ?? '').toLowerCase();
      if (name.includes(query) || id.includes(query) || cur.includes(query)) out.push(c);
      if (out.length >= maxResults) break;
    }
    return out;
  }, [countries, excludedSet, q, maxResults, getLocalizedName]);

  const displayValue = open ? q : selected ? getLocalizedName(selected) : '';

  function openDropdown() {
    if (disabled) return;
    setOpen(true);
    setQ('');
  }

  function closeDropdown() {
    setOpen(false);
    setQ(''); // reset
  }

  function onBlurInput(_e: BlurEvent) {
    setTimeout(() => {
      if (pointerDownInsideRef.current) return;
      closeDropdown();
    }, 150);
  }

  return (
    <View ref={containerRef} style={styles.container}>
      <View style={styles.labelRow}>
        <Text style={{ color: Colors.light.primary }}>{label}</Text>
        {tooltip && (
          <Tooltip content={tooltip}>
            <MaterialIcons name='info' size={18} color={Colors.light.primary} />
          </Tooltip>
        )}
      </View>

      <View style={styles.anchor} ref={anchorRef}>
        <View style={[styles.inputWrap, disabled && styles.disabledOpacity, open && styles.inputWrapActive]}>
          <TextInput
            nativeID={id}
            {...({ name } as any)}
            value={displayValue}
            editable={!disabled}
            placeholder={placeholder ?? t('common.search', 'Search...')}
            placeholderTextColor={Colors.light.placeholderText}
            selectTextOnFocus
            onFocus={openDropdown}
            onBlur={onBlurInput}
            onChangeText={txt => {
              if (!open) setOpen(true);
              setQ(txt);
            }}
            style={styles.input}
          />

          {!!selected && !disabled && !hideClearButton && (
            <Pressable
              onPress={() => {
                onChange(null);
                if (open) setQ('');
              }}
              hitSlop={8}
              style={styles.iconButton}
              accessibilityLabel={t('common.clear', 'Clear')}>
              <MaterialIcons name='close' size={18} color='rgba(0,0,0,0.45)' />
            </Pressable>
          )}

          <Pressable
            onPress={() => (open ? closeDropdown() : openDropdown())}
            hitSlop={8}
            style={styles.iconButton}
            accessibilityLabel={t('common.toggle', 'Toggle')}>
            <MaterialIcons
              name={open ? 'keyboard-arrow-up' : 'keyboard-arrow-down'}
              size={22}
              color='rgba(0,0,0,0.55)'
            />
          </Pressable>
        </View>

        <PortalDropdown
          open={open && !disabled}
          onClose={() => closeDropdown()}
          anchorRef={anchorRef}
          outsideClickRef={containerRef}>
          <ScrollView keyboardShouldPersistTaps='handled' contentContainerStyle={styles.listContent}>
            {filtered.length === 0 ? (
              <View style={styles.empty}>
                <Text>{t('common.noMatches', 'No matches')}</Text>
              </View>
            ) : (
              filtered.map((item, index) => {
                const isDisabled = disabledSet.has(item.id);
                const isSelected = selected?.id === item.id;
                const content = (
                  <Pressable
                    onPress={() => {
                      if (isDisabled) return;
                      onChange(item);
                      pointerDownInsideRef.current = false;
                      closeDropdown();
                    }}
                    style={[styles.option, isDisabled && styles.optionDisabled, isSelected && styles.optionSelected]}>
                    <View style={styles.optionContent}>
                      <Text
                        type={isSelected ? 'defaultSemiBold' : 'default'}
                        style={[
                          styles.optionText,
                          isSelected && styles.optionTextSelected,
                          isDisabled && styles.optionTextDisabled,
                        ]}>
                        {getLocalizedName(item)}
                      </Text>
                      {isDisabled && <MaterialIcons name='info' color={Colors.light.error} size={20} />}
                    </View>

                    {isSelected ? <MaterialIcons name='check' size={18} color={Colors.light.primary} /> : null}
                  </Pressable>
                );

                if (isDisabled) {
                  const side = index < 2 ? 'bottom' : 'top';
                  return (
                    <Tooltip key={item.id} content={t('onboarding.warnings.exchangeControl')} fullWidth side={side}>
                      {content}
                    </Tooltip>
                  );
                }

                return <View key={item.id}>{content}</View>;
              })
            )}
          </ScrollView>
        </PortalDropdown>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 10,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  anchor: {
    position: 'relative',
    zIndex: 1000,
  },
  inputWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#898b8c',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#fff',
  },
  inputWrapActive: {
    borderColor: Colors.light.primary,
  },
  disabledOpacity: {
    opacity: 0.6,
  },
  input: {
    flex: 1,
    outlineStyle: 'none' as any,
    height: 25,
    fontFamily: 'lexend-regular',
  },
  iconButton: {
    paddingHorizontal: 4,
  },
  listContent: {
    alignItems: 'stretch',
  },
  option: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    width: '100%',
    alignSelf: 'stretch',
  },
  optionContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
  },
  optionDisabled: {
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  optionSelected: {
    backgroundColor: 'rgba(48, 87, 133, 0.05)',
  },
  optionTextDisabled: {
    color: 'rgba(0,0,0,0.45)',
  },
  optionText: {
    fontSize: 15,
  },
  optionTextSelected: {
    color: Colors.light.primary,
  },
  empty: {
    padding: 12,
  },
});
